import { Outlet, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { List, Warning } from "@phosphor-icons/react";
import Sidebar from "./Sidebar";

export default function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Auto-close the drawer whenever the route changes (belt-and-braces alongside the
  // per-NavLink onClick — covers programmatic navigations too, e.g. redirects after save).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Detect preview / staging URLs so ops don't accidentally enter real orders here.
  // Production is trucking.portside.nl — anything else (preview.emergentagent.com,
  // *.emergent.host, localhost) surfaces a red banner up top.
  const isPreview = useMemo(() => {
    if (typeof window === "undefined") return false;
    const host = window.location.hostname;
    return host !== "trucking.portside.nl" && host !== "portside.nl";
  }, []);

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="app-layout">
      <Sidebar mobileOpen={mobileOpen} onCloseMobile={() => setMobileOpen(false)} />
      <div className="flex-1 min-w-0 flex flex-col">
        {isPreview && (
          <div
            data-testid="preview-banner"
            className="bg-red-600 text-white text-center text-[11px] sm:text-xs font-semibold px-3 py-1.5 flex items-center justify-center gap-2 tracking-wide"
          >
            <Warning size={14} weight="fill" className="shrink-0" />
            <span>
              PREVIEW ENVIRONMENT — data here does NOT sync to production.{" "}
              <a href="https://trucking.portside.nl" className="underline hover:text-red-100">Use trucking.portside.nl for real work</a>
            </span>
          </div>
        )}
        {/* Mobile top bar — hamburger + brand. Hidden on md+ (sidebar is inline). */}
        <header className="md:hidden sticky top-0 z-30 flex items-center gap-3 px-3 py-2.5 bg-white border-b border-slate-200" data-testid="mobile-topbar">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-button"
            aria-label="Open menu"
            className="p-2 rounded hover:bg-slate-100 active:bg-slate-200 -ml-1"
          >
            <List size={22} weight="bold" />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <img src="https://www.portside.nl/portside-logo-rgb.svg" alt="" className="w-7 h-7 shrink-0" />
            <div className="font-display font-extrabold text-sm tracking-tight text-ps-deep truncate">PORTSIDE</div>
          </div>
        </header>
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
